import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
      
      if (response.data?.branding?.color_scheme) {
        applyColorScheme(response.data.branding.color_scheme);
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const applyColorScheme = (scheme) => {
    const schemes = {
      default: { primary: '#f97316', secondary: '#1e293b', accent: '#ea580c', hsl: '24 95% 53%' },
      blue: { primary: '#2563eb', secondary: '#1e3a5f', accent: '#1d4ed8', hsl: '221 83% 53%' },
      green: { primary: '#16a34a', secondary: '#14532d', accent: '#15803d', hsl: '142 76% 36%' },
      purple: { primary: '#9333ea', secondary: '#3b0764', accent: '#7c3aed', hsl: '271 81% 56%' }
    };
    
    const colors = schemes[scheme] || schemes.default;
    document.documentElement.style.setProperty('--brand-primary', colors.primary);
    document.documentElement.style.setProperty('--brand-secondary', colors.secondary);
    document.documentElement.style.setProperty('--brand-accent', colors.accent);
    document.documentElement.style.setProperty('--primary', colors.hsl);
  };

  const refreshUser = useCallback(async () => {
    const savedToken = localStorage.getItem('token');
    if (savedToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
      try {
        const response = await axios.get(`${API_URL}/auth/me`);
        setUser(response.data);
        if (response.data?.branding?.color_scheme) {
          applyColorScheme(response.data.branding.color_scheme);
        }
      } catch (error) {
        console.error('Failed to refresh user:', error);
      }
    }
  }, []);

  // Used by AuthCallback for Google sign-in to set token + user atomically
  const loginWithToken = useCallback(async (newToken) => {
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
      setLoading(false);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch user after token login:', error);
      setLoading(false);
      throw error;
    }
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name, company) => {
    const response = await axios.post(`${API_URL}/auth/register`, { email, password, name, company });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    applyColorScheme('default');
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, refreshUser, loginWithToken }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
